import { useQuery } from '@tanstack/react-query';
import apiClient from '../libs/apiClient';
import { FormType } from '../types/forms';
import { ApiResponse } from '../types/response';
import { changeToAlphanumeric, formatString, REGEX } from '../utils/stringUtils';

export type ParsedLaunchParams = {
  receptionNo: string;
  formType: FormType | '';
};

type RawLaunchParams = {
  p1?: string | null;
  p2?: string | null;
};

const decryptLaunchParam = async (encryptedParam: string) => {
  const {
    data: { data },
  } = await apiClient.post<ApiResponse<RawLaunchParams>>('/cdus-ui/launch-params/decrypt', {
    param: encryptedParam,
  });

  return data;
};

const parseLaunchParams = (raw: RawLaunchParams): ParsedLaunchParams => {
  const receptionNo = changeToAlphanumeric(formatString(raw.p1 ?? '', 14, REGEX.ANY_ALPHANUMERIC)).toUpperCase();
  const formType = raw.p2 === '0' ? FormType.INDIVIDUAL : raw.p2 === '1' ? FormType.ENTERPRISE : '';
  return { receptionNo, formType };
};

/**
 * Decrypts and parses launch parameters from the backend.
 * Returns parsed { receptionNo, formType }, or undefined if disabled/loading.
 * @param encryptedParam - Encrypted parameter string from URL or sessionStorage
 */
const useDecryptLaunchParam = (encryptedParam: string | null) => {
  return useQuery({
    queryKey: ['launch-params', 'decrypt', encryptedParam],
    queryFn: () => decryptLaunchParam(encryptedParam as string),
    select: parseLaunchParams,
    enabled: Boolean(encryptedParam),
    retry: 0,
    retryOnMount: false,
    staleTime: Infinity,
  });
};

export default useDecryptLaunchParam;
